/**
 * LA TARJETA POR SU CONTORNO (M2): bordes y un cuadrilátero de proporción ID-1.
 *
 * Es el respaldo cuando no hay ancla —ni el código de barras del frente 2012 ni
 * las líneas del MRZ—: el diseño 2026, el 2009 sin su MRZ leído, o un frente cuyo
 * código no se deja decodificar. A diferencia de la mancha clara de Otsu
 * (geometria.ts), no necesita que la tarjeta sea más clara que el fondo: busca
 * BORDES, así que sirve sobre un fondo claro, o más claro que la tarjeta.
 *
 * Cómo: gradiente (Sobel) sobre la imagen reducida, transformada de Hough con el
 * voto orientado por el gradiente, las rectas más votadas, y de a dos pares de
 * rectas casi paralelas que se cortan casi en ángulo recto, el cuadrilátero con
 * la proporción de la tarjeta y más borde a lo largo de sus cuatro lados.
 *
 * Lo que NO hace: inventar un lado que no se ve. Si la foto corta la tarjeta, no
 * hay cuatro bordes y devuelve null. Todo puro, sin dependencias: entra una
 * matriz de píxeles, salen cuatro esquinas.
 */
import { Punto } from "./geometria";
import { ImagenPlana } from "./quality";
import { TARJETA_ID1 } from "./tuning";

type Cuadrilatero = [Punto, Punto, Punto, Punto];

/** Lado mayor de la imagen de trabajo. Los bordes de una tarjeta se ven de sobra. */
const LADO_DE_TRABAJO = 360;
/** Cuántas rectas se consideran. */
const RECTAS = 18;
/** Tolerancias del cuadrilátero. */
const TOLERANCIA_PARALELAS_GRADOS = 6;
const TOLERANCIA_RECTO_GRADOS = 8;
const TOLERANCIA_PROPORCION = 0.1;
/** Fracción mínima de cada lado, y del perímetro, que tiene que ser borde. */
const BORDE_MINIMO_POR_LADO = 0.55;
const BORDE_MINIMO_TOTAL = 0.75;
/** La tarjeta ocupa al menos esto de la foto. */
const AREA_MINIMA = 0.15;

interface Recta {
  /** Ángulo de la normal, en grados [0, 180). */
  theta: number;
  rho: number;
  votos: number;
}

/** Luma reducida a LADO_DE_TRABAJO, promediando bloques. */
function lumaReducida(img: ImagenPlana): { g: Float32Array; w: number; h: number; f: number } {
  const f = Math.max(1, Math.max(img.width, img.height) / LADO_DE_TRABAJO);
  const w = Math.max(1, Math.round(img.width / f));
  const h = Math.max(1, Math.round(img.height / f));
  const g = new Float32Array(w * h);
  const n = new Float32Array(w * h);
  for (let y = 0; y < img.height; y++) {
    const yy = Math.min(h - 1, Math.floor(y / f));
    for (let x = 0; x < img.width; x++) {
      const xx = Math.min(w - 1, Math.floor(x / f));
      const i = (y * img.width + x) * 4;
      g[yy * w + xx] += (img.data[i] * 299 + img.data[i + 1] * 587 + img.data[i + 2] * 114) / 1000;
      n[yy * w + xx] += 1;
    }
  }
  for (let i = 0; i < g.length; i++) g[i] /= n[i] || 1;
  return { g, w, h, f };
}

/** Borde (magnitud y ángulo del gradiente de Sobel), con un umbral relativo. */
function bordes(g: Float32Array, w: number, h: number) {
  const mag = new Float32Array(w * h);
  const ang = new Float32Array(w * h);
  let max = 0;
  for (let y = 1; y < h - 1; y++)
    for (let x = 1; x < w - 1; x++) {
      const p = (dx: number, dy: number) => g[(y + dy) * w + x + dx];
      const gx = p(1, -1) + 2 * p(1, 0) + p(1, 1) - p(-1, -1) - 2 * p(-1, 0) - p(-1, 1);
      const gy = p(-1, 1) + 2 * p(0, 1) + p(1, 1) - p(-1, -1) - 2 * p(0, -1) - p(1, -1);
      const m = Math.hypot(gx, gy);
      mag[y * w + x] = m;
      ang[y * w + x] = Math.atan2(gy, gx);
      if (m > max) max = m;
    }
  // Un borde es un salto de luz que se ve: al menos 24 niveles en el kernel de Sobel
  // (6 de luma) y una fracción del salto más fuerte de la foto.
  const umbral = Math.max(24, max * 0.12);
  const es = new Uint8Array(w * h);
  for (let i = 0; i < es.length; i++) es[i] = mag[i] >= umbral ? 1 : 0;
  return { es, ang };
}

/** Hough con el voto orientado: cada borde vota solo por las rectas cercanas a su propia orientación. */
function rectas(es: Uint8Array, ang: Float32Array, w: number, h: number): Recta[] {
  const diag = Math.ceil(Math.hypot(w, h));
  const nr = 2 * diag + 1;
  const acc = new Float32Array(180 * nr);
  const cos = Array.from({ length: 180 }, (_, t) => Math.cos((t * Math.PI) / 180));
  const sin = Array.from({ length: 180 }, (_, t) => Math.sin((t * Math.PI) / 180));
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      if (!es[y * w + x]) continue;
      let t0 = Math.round((ang[y * w + x] * 180) / Math.PI);
      t0 = ((t0 % 180) + 180) % 180;
      for (let d = -3; d <= 3; d++) {
        const t = (t0 + d + 180) % 180;
        const rho = Math.round(x * cos[t] + y * sin[t]) + diag;
        acc[t * nr + rho] += 1;
      }
    }
  // Picos, con supresión de no máximos en una ventana de ángulo y distancia.
  const candidatos: Recta[] = [];
  const minimo = Math.min(w, h) * 0.15;
  for (let t = 0; t < 180; t++)
    for (let r = 0; r < nr; r++) {
      const v = acc[t * nr + r];
      if (v < minimo) continue;
      let esMax = true;
      for (let dt = -4; dt <= 4 && esMax; dt++)
        for (let dr = -6; dr <= 6; dr++) {
          if (!dt && !dr) continue;
          const tt = t + dt, rr = r + dr;
          if (tt < 0 || tt >= 180 || rr < 0 || rr >= nr) continue;
          if (acc[tt * nr + rr] > v) { esMax = false; break; }
        }
      if (esMax) candidatos.push({ theta: t, rho: r - diag, votos: v });
    }
  return candidatos.sort((a, b) => b.votos - a.votos).slice(0, RECTAS);
}

function corte(a: Recta, b: Recta): Punto | null {
  const ta = (a.theta * Math.PI) / 180, tb = (b.theta * Math.PI) / 180;
  const det = Math.cos(ta) * Math.sin(tb) - Math.sin(ta) * Math.cos(tb);
  if (Math.abs(det) < 1e-6) return null;
  return {
    x: (a.rho * Math.sin(tb) - b.rho * Math.sin(ta)) / det,
    y: (b.rho * Math.cos(ta) - a.rho * Math.cos(tb)) / det,
  };
}

const difAngulo = (a: number, b: number) => {
  const d = Math.abs(a - b) % 180;
  return Math.min(d, 180 - d);
};

/** Fracción de puntos de un segmento que caen sobre un borde (con un píxel de holgura). */
function soporte(es: Uint8Array, w: number, h: number, a: Punto, b: Punto): number {
  const n = Math.max(10, Math.round(Math.hypot(b.x - a.x, b.y - a.y)));
  let si = 0;
  for (let k = 1; k < n; k++) {
    const x = Math.round(a.x + ((b.x - a.x) * k) / n);
    const y = Math.round(a.y + ((b.y - a.y) * k) / n);
    let hay = false;
    for (let dy = -1; dy <= 1 && !hay; dy++)
      for (let dx = -1; dx <= 1; dx++) {
        const xx = x + dx, yy = y + dy;
        if (xx >= 0 && yy >= 0 && xx < w && yy < h && es[yy * w + xx]) { hay = true; break; }
      }
    if (hay) si++;
  }
  return si / (n - 1);
}

/**
 * De qué lado del borde está lo claro, a lo largo de un lado: +1 si adentro es
 * más claro que afuera, -1 si al revés, 0 si no es parejo. Una tarjeta sobre un
 * fondo es más clara (o más oscura) que él en TODO su contorno; un rectángulo
 * que arman la foto y el texto de adentro, no: de un lado está la foto, del otro
 * el papel. Es lo que separa la tarjeta de los rectángulos interiores.
 */
function ladoClaro(g: Float32Array, w: number, h: number, a: Punto, b: Punto, centro: Punto): number {
  const n = 24;
  let mas = 0, menos = 0, iguales = 0;
  for (let k = 2; k < n - 1; k++) {
    const p = { x: a.x + ((b.x - a.x) * k) / n, y: a.y + ((b.y - a.y) * k) / n };
    const dx = centro.x - p.x, dy = centro.y - p.y;
    const d = Math.hypot(dx, dy) || 1;
    const muestra = (s: number) => {
      const x = Math.round(p.x + (dx / d) * s), y = Math.round(p.y + (dy / d) * s);
      return x >= 0 && y >= 0 && x < w && y < h ? g[y * w + x] : null;
    };
    const adentro = muestra(4), afuera = muestra(-4);
    if (adentro === null || afuera === null) continue;
    if (adentro - afuera > 4) mas++;
    else if (afuera - adentro > 4) menos++;
    else iguales++;
  }
  // Sobre los puntos que se pudieron medir: cerca del borde de la foto, afuera no hay nada.
  const total = mas + menos + iguales;
  if (total < 6) return 0;
  if (mas >= total * 0.5 && mas > 2 * menos) return 1;
  if (menos >= total * 0.5 && menos > 2 * mas) return -1;
  return 0;
}

/** Si un punto está adentro del cuadrilátero convexo (en cualquier orden de giro). */
function dentroDe(q: Cuadrilatero, x: number, y: number): boolean {
  let signo = 0;
  for (let k = 0; k < 4; k++) {
    const a = q[k], b = q[(k + 1) % 4];
    const c = Math.sign((b.x - a.x) * (y - a.y) - (b.y - a.y) * (x - a.x));
    if (c === 0) continue;
    if (signo === 0) signo = c;
    else if (c !== signo) return false;
  }
  return true;
}

/** El mismo cuadrilátero agrandado (o achicado) desde su centro. */
function escalado(q: Cuadrilatero, f: number): Cuadrilatero {
  const cx = (q[0].x + q[2].x) / 2, cy = (q[0].y + q[2].y) / 2;
  return q.map((p) => ({ x: cx + (p.x - cx) * f, y: cy + (p.y - cy) * f })) as Cuadrilatero;
}

/** Fracción de bordes en la franja de afuera (hasta un 15 % más grande), dentro de la foto. */
function densidadAfuera(es: Uint8Array, w: number, h: number, q: Cuadrilatero): number {
  const grande = escalado(q, 1.15), justo = escalado(q, 1.03);
  let n = 0, b = 0;
  for (let y = 0; y < h; y += 2)
    for (let x = 0; x < w; x += 2)
      if (dentroDe(grande, x, y) && !dentroDe(justo, x, y)) { n++; b += es[y * w + x]; }
  return n > 20 ? b / n : 0;
}

/** Fracción de bordes adentro, lejos del contorno. */
function densidadAdentro(es: Uint8Array, w: number, h: number, q: Cuadrilatero): number {
  const chico = escalado(q, 0.9);
  let n = 0, b = 0;
  for (let y = 0; y < h; y += 2)
    for (let x = 0; x < w; x += 2)
      if (dentroDe(chico, x, y)) { n++; b += es[y * w + x]; }
  return n > 0 ? b / n : 0;
}

/** Ordena cuatro puntos como SI, SD, ID, II. */
function ordenar(p: Punto[]): Cuadrilatero {
  const porSuma = [...p].sort((a, b) => a.x + a.y - (b.x + b.y));
  const porResta = [...p].sort((a, b) => a.x - a.y - (b.x - b.y));
  return [porSuma[0], porResta[3], porSuma[3], porResta[0]];
}

/**
 * Las cuatro esquinas de la tarjeta (SI, SD, ID, II) en coordenadas de la
 * imagen, o null si no hay un contorno de tarjeta que se vea entero.
 */
export function tarjetaPorContorno(img: ImagenPlana): Cuadrilatero | null {
  if (img.width < 80 || img.height < 50) return null;
  const { g, w, h, f } = lumaReducida(img);
  const { es, ang } = bordes(g, w, h);
  const rs = rectas(es, ang, w, h);

  // Pares de rectas casi paralelas, separadas lo que puede medir un lado.
  const pares: [Recta, Recta][] = [];
  for (let i = 0; i < rs.length; i++)
    for (let j = i + 1; j < rs.length; j++) {
      const a = rs[i], b = rs[j];
      if (difAngulo(a.theta, b.theta) > TOLERANCIA_PARALELAS_GRADOS) continue;
      // Con thetas a ambos lados de 0/180 la misma recta tiene rho de signo opuesto.
      const rb = Math.abs(a.theta - b.theta) > 90 ? -b.rho : b.rho;
      if (Math.abs(a.rho - rb) < Math.min(w, h) * 0.2) continue;
      pares.push([a, b]);
    }

  let mejor: { q: Cuadrilatero; puntaje: number } | null = null;
  const margen = Math.max(w, h) * 0.02;
  for (let i = 0; i < pares.length; i++)
    for (let j = i + 1; j < pares.length; j++) {
      const [a1, a2] = pares[i];
      const [b1, b2] = pares[j];
      if (Math.abs(difAngulo(a1.theta, b1.theta) - 90) > TOLERANCIA_RECTO_GRADOS) continue;
      const puntos = [corte(a1, b1), corte(a1, b2), corte(a2, b2), corte(a2, b1)];
      if (puntos.some((p) => !p || p.x < -margen || p.y < -margen || p.x > w + margen || p.y > h + margen)) continue;
      const q = ordenar(puntos as Punto[]);
      const lados = [0, 1, 2, 3].map((k) => Math.hypot(q[(k + 1) % 4].x - q[k].x, q[(k + 1) % 4].y - q[k].y));
      const ancho = (lados[0] + lados[2]) / 2;
      const alto = (lados[1] + lados[3]) / 2;
      // Apaisada: la cascada ya giró la foto con el texto derecho.
      if (alto <= 0 || ancho < alto) continue;
      if (Math.abs(ancho / alto - TARJETA_ID1.relacionDeLados) / TARJETA_ID1.relacionDeLados > TOLERANCIA_PROPORCION) continue;
      if ((ancho * alto) / (w * h) < AREA_MINIMA) continue;
      const porLado = [0, 1, 2, 3].map((k) => soporte(es, w, h, q[k], q[(k + 1) % 4]));
      if (porLado.some((s) => s < BORDE_MINIMO_POR_LADO)) continue;
      const total = porLado.reduce((s, x) => s + x, 0) / 4;
      if (total < BORDE_MINIMO_TOTAL) continue;
      const centro = { x: (q[0].x + q[2].x) / 2, y: (q[0].y + q[2].y) / 2 };
      const claros = [0, 1, 2, 3].map((k) => ladoClaro(g, w, h, q[k], q[(k + 1) % 4], centro));
      // Los lados que se pudieron juzgar tienen que coincidir, y al menos dos: un lado sobre el
      // borde de la foto, o con el fondo del mismo tono, no dice nada.
      const juzgados = claros.filter((c) => c !== 0);
      if (juzgados.length < 2 || juzgados.some((c) => c !== juzgados[0])) continue;
      // Afuera de la tarjeta hay fondo, con menos detalle que el texto de adentro. Un rectángulo
      // interior (la foto y un renglón, en una foto recortada al ras) tiene afuera más tarjeta.
      if (densidadAfuera(es, w, h, q) > 0.6 * densidadAdentro(es, w, h, q)) continue;
      // Entre candidatas buenas gana la que más borde tiene y, a igualdad, la más grande:
      // el texto de adentro también arma rectángulos, más chicos.
      const puntaje = total * Math.sqrt((ancho * alto) / (w * h));
      if (!mejor || puntaje > mejor.puntaje) mejor = { q, puntaje };
    }
  if (!mejor) return null;
  return mejor.q.map((p) => ({ x: p.x * f, y: p.y * f })) as Cuadrilatero;
}
